import queryString from "query-string";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate } from "react-router";
import i18next from "src/i18n/config";
import { resetPassword } from "src/ui/containers/account/api";
import { AuthFlowContainer } from "src/ui/shared/AuthFlowContainer";
import { makeStyles } from "tss-react/mui";

import { InputAdornment, Typography, useTheme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { CustomCard, CustomCardContent, CustomCardHeader, CustomCardTitle } from "@shared/Card";
import { PrimaryButton } from "@shared/CustomButton";
import { TextField } from "@shared/form/TextField";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

const useStyles = makeStyles()((theme) => ({
    card: { width: "100%" },
    description: { margin: 0, marginBottom: theme.spacing(5) },
}));

type FormValues = {
    password: string;
    passwordRepeat: string;
};

const PasswordResetPage = () => {
    const [showPassword, setShowwPassord] = React.useState(false);
    const [showRepeatPassword, setShowwRepeatPassord] = React.useState(false);

    const { classes } = useStyles();
    const navigate = useNavigate();
    const theme = useTheme();
    const { t } = useTranslation(["pages", "common"]);

    const { search } = useLocation();
    const { id, token } = queryString.parse(search, {
        decode: false,
    });

    const onSubmit = async (values: FormValues) => {
        if (!id || !token) {
            return null;
        }

        try {
            await resetPassword({
                id: id.toString(),
                token: token.toString(),
                password: values.password,
                passwordRepeat: values.passwordRepeat,
            });
            showSuccessToast(t("passwordResetPage.toasts.success"));
            void navigate("/login");
        } catch (err) {
            const error = err as Error;
            showErrorToast(error.message);
        }
    };

    const onValidate = (values: FormValues) => {
        const errors: Partial<FormValues> = {};

        if (!values.password) {
            errors.password = t("passwordResetPage.validation.passwordRequired");
        }

        if (!values.passwordRepeat) {
            errors.passwordRepeat = t("passwordResetPage.validation.passwordRepeatRequired");
        }

        if (values.password !== values.passwordRepeat) {
            errors.passwordRepeat = t("passwordResetPage.validation.passwordsDoNotMatch");
        }

        return errors;
    };

    const hasResetCredentials = Boolean(id && token);

    // Toasting and navigating from the render body made React Router warn and fired the toast again on every
    // re-render; the redirect itself belongs in the returned element.
    React.useEffect(() => {
        if (!hasResetCredentials) {
            showErrorToast(t("passwordResetPage.toasts.error"));
        }
    }, [hasResetCredentials, t]);

    if (!hasResetCredentials) {
        return <Navigate to="/login" replace />;
    }

    return (
        <AuthFlowContainer>
            <CustomCard className={classes.card}>
                <CustomCardHeader title={<CustomCardTitle variant="h6">{t("passwordResetPage.title")}</CustomCardTitle>} />
                <CustomCardContent>
                    <Form<FormValues>
                        onSubmit={onSubmit}
                        validate={onValidate}
                        render={({ handleSubmit, submitting }) => (
                            <form onSubmit={handleSubmit}>
                                <Typography className={classes.description}>{t("passwordResetPage.description")}</Typography>

                                <TextField
                                    placeholder={t("passwordResetPage.newPasswordPlaceholder")}
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    style={{ marginBottom: theme.spacing(1) }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <i className="fa-solid fa-fw fa-key" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <i
                                                        className="fa-solid fa-eye"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setShowwPassord((prev) => !prev)}
                                                    />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <TextField
                                    placeholder={t("passwordResetPage.repeatPasswordPlaceholder")}
                                    name="passwordRepeat"
                                    type={showRepeatPassword ? "text" : "password"}
                                    fullWidth
                                    style={{ marginBottom: theme.spacing(4) }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <i className="fa-solid fa-fw fa-key" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <i
                                                        className="fa-solid fa-eye"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={() => setShowwRepeatPassord((prev) => !prev)}
                                                    />
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />
                                <PrimaryButton type="submit" loading={submitting} disabled={submitting} fullWidth>
                                    {t("common:button.submit")}
                                </PrimaryButton>
                            </form>
                        )}
                    />
                </CustomCardContent>
            </CustomCard>
        </AuthFlowContainer>
    );
};

export default withMetaDescription(PasswordResetPage, {
    title: i18next.t("pages:passwordResetPage.metaTitle"),
    description: i18next.t("pages:passwordResetPage.metaDescription"),
});
